import { useHasEverSubscribed } from "@/hooks/use-has-ever-subscribed";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";

type BillingCadence = "monthly" | "yearly";

type BeginCheckoutOptions = {
  priceId: string;
  planName: string;
  cadence: BillingCadence;
  onCheckoutOpened?: () => void;
};

// Request-a-trial flag only — the real length (30, or 60 for referred
// merchants) is decided server-side in createCheckoutSession.
const TRIAL_DAYS = 30;

/**
 * Opens Stripe Checkout for a plan. First-time subscribers get the single
 * 30-day trial (card on file, first charge on day 31); returning subscribers
 * are charged immediately. There is no separate no-card app trial anymore, so
 * no "you'll forfeit remaining free days" confirmation is needed.
 */
export function useTrialCheckoutConfirmation({
  userEmail,
  userId,
}: {
  userEmail?: string | null;
  userId?: string | null;
}) {
  const { openCheckout, loading: checkoutLoading, checkoutDialog } = useStripeCheckout();
  const { loading: everLoading, hasEverSubscribed } = useHasEverSubscribed();

  const beginCheckout = async (options: BeginCheckoutOptions) => {
    await openCheckout({
      priceId: options.priceId,
      customerEmail: userEmail || undefined,
      userId: userId || undefined,
      trialDays: hasEverSubscribed ? undefined : TRIAL_DAYS,
    });
    options.onCheckoutOpened?.();
    return true;
  };

  return {
    beginCheckout,
    confirmationDialog: checkoutDialog,
    checkoutLoading: checkoutLoading || (!!userId && everLoading),
    hasEverSubscribed,
  };
}
